import { useEffect } from 'react';
import { Outlet, useParams, generatePath, useLocation } from 'react-router-dom';
import {
  IconLoader,
  Tabs,
  Table,
  useFilters,
  useQueryState,
  Filters,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  getUser,
  getCards,
  getTransactions,
  onSortTransactions,
  onFilterTransactions,
} from './store/slices/item';
import CardGeneral from '../../components/cards/cards-list';
import {
  transactionsColumnsLabels,
  transactionsFiltersSchema,
  transactionsColumns,
  transactionsSchema,
} from './variables/transactions';
import { Transaction } from './types';
import formatTransacationsList from './helpers/format-transactions';

import { CardsRoutes } from '../../routes';

import cn from './cards.module.css';

const CardItem = () => {
  const {
    user,
    cards,
    changeStatusInProgress,
    transactions,
    changeStatusSuccess,
  } = useAppSelector((state) => state.cards.item);

  const { search } = useLocation();

  const { filters, sort, onSortChange, onFilterChange } =
    useQueryState(transactionsSchema);

  const { cardId, id } = useParams();

  const dispatch = useAppDispatch();

  const onSort = (name: string) => {
    onSortChange(name);

    dispatch(
      onSortTransactions({
        name,
        direction: sort.sortDir === 'asc' ? 'desc' : 'asc',
      }),
    );
  };

  const onFilter = (values: Filters) => {
    const body = { ...values };

    onFilterChange(values);

    if (values.date) {
      body.date.value = values.date.value?.map((item) =>
        new Date(item).toISOString(),
      );
    }

    dispatch(onFilterTransactions(body));
  };

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: transactionsFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useEffect(() => {
    if (cardId) {
      dispatch(getUser(cardId));
      dispatch(getCards());
    }
  }, [dispatch, cardId]);

  useEffect(() => {
    if (id) {
      dispatch(
        getTransactions({
          name: sort?.sortProp,
          direction: sort?.sortDir as 'asc' | 'desc',
          filters,
          cardId: id,
        }),
      );
    }
  }, [dispatch, id, sort, filters]);

  useEffect(() => {
    let title =
      user?.firstName || user?.lastName
        ? `Cards: ${user?.firstName || ''} ${user?.lastName || ''}`
        : 'Cards';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [user?.firstName, user?.lastName]);

  let content;

  if (transactions.isLoading) {
    content = (
      <div className={cn.transactionsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <div className={cn.transactionTableWrapper}>
        <Table
          columns={transactionsColumns}
          data={
            formatTransacationsList(
              transactions?.values as Transaction[],
              onFilter,
              filters,
              cardId,
              id,
            ) || []
          }
          onSort={(name) => onSort(name)}
          filters={filters}
          sort={sort}
          columnsLabels={transactionsColumnsLabels}
          onFilterChange={(values) => onFilter(values)}
          filtersComponent={filtersComponent}
          handleSubmit={handleSubmit}
          isLoading={transactions.requestInProgress}
          tableFormClassName={cn.transactionTableForm}
          tableHeadCellClassName={cn.transactionTableHeadCell}
          clickable
        />
      </div>
    );
  }

  return (
    <>
      <CardGeneral
        isLoading={cards.isLoading}
        cards={cards.values}
        user={user}
        changeStatusInProgress={changeStatusInProgress}
        cardId={cardId}
        id={id}
        search={search}
        changeStatusSuccess={changeStatusSuccess}
      />
      <div className={cn.transactions}>
        <div className={cn.transactionsTitle}>Transactions</div>
        {!cards.isLoading && cards.values?.length && (
          <Tabs
            tabs={cards.values.map((card) => ({
              name: `${card.paymentSystem} ${card.cardType} ${card.pan}`,
              path: generatePath(CardsRoutes.CARD_INFO_TRANSACTIONS, {
                cardId: cardId as string,
                id: card.id,
              }),
            }))}
            enableRouting
          />
        )}
        {content}
      </div>
      <Outlet />
    </>
  );
};

export default CardItem;
